import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Button, Grid, Divider,
    Chip, alpha, useTheme, CircularProgress, Stack, Table,
    TableBody, TableCell, TableHead, TableRow, Snackbar, Alert,
    Skeleton, Tooltip,
} from '@mui/material';
import {
    CreditCard as CardIcon,
    History as HistoryIcon,
    Star as PremiumIcon,
    CheckCircle as ActiveIcon,
    AutoAwesome as AIIcon,
    Bolt as TopupIcon,
    OpenInNew as PortalIcon,
    TrendingUp as TrendingIcon,
    Receipt as ReceiptIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import confetti from 'canvas-confetti';
import { billingApi, invoicesApi } from '../services/api';

const ACCENT   = '#6366f1';
const GREEN    = '#10d98a';
const AMBER    = '#f59e0b';

const Billing = () => {
    const theme   = useTheme();
    const isDark  = theme.palette.mode === 'dark';
    const location = useLocation();

    const [, setUser]        = useState(null);
    const [userData,  setUserData]  = useState(null);
    const [usageData, setUsageData] = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [showCelebration, setShowCelebration] = useState(false);
    const [invoices,  setInvoices]  = useState([]);
    const [invoicesLoadError, setInvoicesLoadError] = useState(false);
    const [invoicesHasMore, setInvoicesHasMore] = useState(false);
    const [invoicesOffset, setInvoicesOffset] = useState(0);
    const [invoicesLoadingMore, setInvoicesLoadingMore] = useState(false);
    const [invoicesPdfLoading, setInvoicesPdfLoading] = useState({});
    const [pdfErrorSnack, setPdfErrorSnack] = useState(false);

    const queryParams = new URLSearchParams(location.search);
    const isSuccess  = queryParams.get('success')  === 'true';
    const isCanceled = queryParams.get('canceled') === 'true';

    useEffect(() => {
        if (isSuccess && !showCelebration) triggerConfetti();
    }, [isSuccess, showCelebration]); // eslint-disable-line react-hooks/exhaustive-deps

    const triggerConfetti = () => {
        setShowCelebration(true);
        const duration = 7 * 1000;
        const animationEnd = Date.now() + duration;
        const scalar   = 2;
        const star     = confetti.shapeFromText({ text: '⭐', scalar });
        const balloon  = confetti.shapeFromText({ text: '🎈', scalar });
        const party    = confetti.shapeFromText({ text: '🎉', scalar });
        const pinata   = confetti.shapeFromText({ text: '🪅', scalar });
        const sparkling = confetti.shapeFromText({ text: '✨', scalar });

        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();
            if (timeLeft <= 0) return clearInterval(interval);
            confetti({ particleCount: 3, angle: 90, spread: 45, origin: { x: 0.5, y: 1 }, startVelocity: 45, gravity: 1.2, shapes: [star, balloon, party, pinata, sparkling], ticks: 300 });
            confetti({ particleCount: 2, angle: 270, spread: 180, origin: { x: Math.random(), y: -0.1 }, startVelocity: 5, gravity: 0.5, shapes: [star, sparkling], ticks: 400 });
            if (timeLeft % 1000 < 100) {
                confetti({ particleCount: 40, angle: 60,  spread: 55, origin: { x: 0, y: 0.6 }, colors: ['#26ccff','#a25afd','#ff5e7e','#88ff5a','#fcff42','#ffa62d','#ff36ff'] });
                confetti({ particleCount: 40, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors: ['#26ccff','#a25afd','#ff5e7e','#88ff5a','#fcff42','#ffa62d','#ff36ff'] });
            }
        }, 150);
    };

    const handleDownloadPdf = async (invoice) => {
        setInvoicesPdfLoading(prev => ({ ...prev, [invoice.invoice_id]: true }));
        try {
            const { pdf_url: url } = await invoicesApi.getPdfUrl(invoice.invoice_id);
            if (url) {
                const link = document.createElement('a');
                link.href = url;
                link.download = `invoice-${invoice.invoice_number || invoice.invoice_id}.pdf`;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch {
            setPdfErrorSnack(true);
        } finally {
            setInvoicesPdfLoading(prev => ({ ...prev, [invoice.invoice_id]: false }));
        }
    };

    const handleLoadMoreInvoices = async () => {
        setInvoicesLoadingMore(true);
        try {
            const r = await invoicesApi.list(invoicesOffset, 20);
            setInvoices(prev => [...prev, ...(r.invoices || [])]);
            setInvoicesHasMore(r.has_more || false);
            setInvoicesOffset(prev => prev + (r.total_returned || 0));
        } catch { /* non-critical */ }
        finally { setInvoicesLoadingMore(false); }
    };

    const handleManagePortal = async () => {
        try {
            const { url } = await billingApi.createPortalSession();
            if (url) window.location.href = url;
        } catch {
            alert('Failed to open billing management. Please try again later.');
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                if (db) {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) setUserData(userDoc.data());
                }
                try { const u = await billingApi.getUsage(); setUsageData(u); } catch { /* non-critical */ }
                try {
                    const r = await invoicesApi.list(0, 20);
                    setInvoices(r.invoices || []);
                    setInvoicesHasMore(r.has_more || false);
                    setInvoicesOffset(r.total_returned || 0);
                } catch { setInvoicesLoadError(true); }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    const currentPlan = userData?.plan || 'free';
    const isPremium   = currentPlan !== 'free';
    const isActive    = userData?.status === 'active';

    const usedPct    = usageData && usageData.plan_limit > 0
        ? Math.min(100, (usageData.request_count / usageData.plan_limit) * 100) : 0;
    const quotaColor = usedPct >= 100 ? '#ef4444' : usedPct > 80 ? AMBER : GREEN;

    // ── Shared style helpers ──────────────────────────────────────────────────
    // Card: inherit global MuiCard overrides but add bigger radius + no hover lift
    const cardSx = {
        borderRadius: '24px',
        height: '100%',
        '&:hover': { transform: 'none' }, // suppress global lift on billing cards
    };

    // Subtle inner panel (plan benefits box, stripe ID box, etc.)
    const innerPanelSx = (color = ACCENT) => ({
        p: 2.5,
        borderRadius: '16px',
        bgcolor: alpha(color, isDark ? 0.07 : 0.06),
        border: `1px dashed ${alpha(color, isDark ? 0.25 : 0.3)}`,
    });

    // Small square icon container
    const iconBoxSx = (color = ACCENT) => ({
        width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
        bgcolor: alpha(color, isDark ? 0.15 : 0.1),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    });

    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: { xs: 2, md: 0 } }}>

            {/* ── Page header ─────────────────────────────────────────────── */}
            <Box sx={{ mb: 5 }}>
                <Typography sx={{
                    fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.12em',
                    textTransform: 'uppercase', color: GREEN, mb: 0.5,
                }}>
                    Account
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 1 }}>
                    Billing &amp; Subscription
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '1rem' }}>
                    Manage your plan, usage, and payment details.
                </Typography>
            </Box>

            {/* ── Success banner ───────────────────────────────────────────── */}
            {isSuccess && (
                <Box sx={{
                    mb: 4, p: 3.5, borderRadius: '20px',
                    bgcolor: alpha(GREEN, isDark ? 0.1 : 0.07),
                    border: `1px solid ${alpha(GREEN, 0.3)}`,
                    display: 'flex', alignItems: 'center', gap: 2.5,
                    animation: 'fadeInUp 0.5s ease-out',
                    '@keyframes fadeInUp': { from: { opacity: 0, transform: 'translateY(16px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: GREEN, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 24px ${alpha(GREEN, 0.45)}` }}>
                        <ActiveIcon sx={{ color: '#fff', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: GREEN, mb: 0.3 }}>
                            Welcome to the Club! 🚀
                        </Typography>
                        <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            Your payment was successful and your account has been upgraded. Enjoy the full power of AI.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* ── Cancelled banner ─────────────────────────────────────────── */}
            {isCanceled && (
                <Box sx={{
                    mb: 4, p: 3, borderRadius: '20px',
                    bgcolor: alpha(ACCENT, isDark ? 0.07 : 0.05),
                    border: `1px solid ${alpha(ACCENT, 0.2)}`,
                    display: 'flex', alignItems: 'center', gap: 2,
                }}>
                    <Typography sx={{ fontSize: 26, flexShrink: 0 }}>ℹ️</Typography>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: ACCENT, mb: 0.2 }}>Checkout Canceled</Typography>
                        <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            Your payment was not completed. No charges were made.
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* ── Row 1: Current Plan + Payment Method ─────────────────────── */}
            <Grid container spacing={3} sx={{ mb: 3 }}>

                {/* Current Plan card */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Card sx={{ ...cardSx, position: 'relative', overflow: 'hidden' }}>
                        {/* ambient glow blob */}
                        <Box sx={{
                            position: 'absolute', top: -60, right: -60, width: 200, height: 200,
                            borderRadius: '50%', pointerEvents: 'none',
                            background: `radial-gradient(circle, ${alpha(isPremium ? GREEN : ACCENT, isDark ? 0.18 : 0.1)} 0%, transparent 70%)`,
                        }} />

                        <CardContent sx={{ p: 4, position: 'relative' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: GREEN, mb: 0.5 }}>
                                        Current Plan
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', textTransform: 'capitalize' }}>
                                        {currentPlan.replace('_', ' ')}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={isActive ? 'ACTIVE' : 'INACTIVE'}
                                    size="small"
                                    sx={{
                                        fontWeight: 800, fontSize: '0.68rem', letterSpacing: '0.08em',
                                        bgcolor: isActive ? alpha(GREEN, 0.12) : alpha(theme.palette.divider, 0.3),
                                        color:   isActive ? GREEN : theme.palette.text.secondary,
                                        border:  `1px solid ${isActive ? alpha(GREEN, 0.35) : alpha(theme.palette.divider, 0.4)}`,
                                    }}
                                />
                            </Stack>

                            {/* Plan benefits */}
                            <Box sx={{ ...innerPanelSx(isPremium ? GREEN : ACCENT), mb: 4 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <PremiumIcon sx={{ fontSize: 18, color: isPremium ? GREEN : ACCENT }} />
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Plan Benefits</Typography>
                                </Stack>
                                <Typography color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.7 }}>
                                    {isPremium
                                        ? 'You have full access to all AI features, multi-platform posting, and the advanced engagement moderation queue.'
                                        : 'You are currently on the Free tier. Upgrade to Pro to unlock AI-powered content analysis and multi-platform scheduling.'}
                                </Typography>
                            </Box>

                            {/* CTAs */}
                            <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                <Button
                                    variant="contained"
                                    component={Link}
                                    to="/app/pricing"
                                    startIcon={<PremiumIcon />}
                                    sx={{
                                        borderRadius: '100px', fontWeight: 800, px: 3, py: 1.2,
                                        bgcolor: ACCENT, color: '#fff',
                                        boxShadow: `0 6px 20px ${alpha(ACCENT, 0.4)}`,
                                        '&:hover': { bgcolor: '#818cf8', boxShadow: `0 8px 28px ${alpha(ACCENT, 0.5)}` },
                                    }}
                                >
                                    {isPremium ? 'Change Plan' : 'Upgrade to Pro'}
                                </Button>
                                {isPremium && userData?.payment_gateway !== 'razorpay' && (
                                    <Button
                                        variant="outlined"
                                        onClick={handleManagePortal}
                                        startIcon={<PortalIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            borderRadius: '100px', fontWeight: 700, px: 3,
                                            borderColor: alpha(theme.palette.divider, 0.4),
                                            color: theme.palette.text.secondary,
                                            '&:hover': { borderColor: '#ef4444', color: '#ef4444', bgcolor: alpha('#ef4444', 0.05) },
                                        }}
                                    >
                                        Cancel Subscription
                                    </Button>
                                )}
                                {isPremium && userData?.payment_gateway === 'razorpay' && (
                                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', alignSelf: 'center' }}>
                                        To cancel, manage your subscription via the Razorpay customer portal.
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Payment Method card */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: 4 }}>

                            {/* Payment Method */}
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                                <Box sx={iconBoxSx(ACCENT)}>
                                    <CardIcon sx={{ color: ACCENT, fontSize: 20 }} />
                                </Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Payment Method</Typography>
                            </Stack>

                            {userData?.payment_gateway === 'razorpay' ? (
                                <Box>
                                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1.5 }}>
                                        Razorpay Subscription ID
                                    </Typography>
                                    <Box sx={{
                                        p: 1.5, borderRadius: '10px',
                                        bgcolor: alpha(theme.palette.divider, isDark ? 0.12 : 0.06),
                                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                                    }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                            {userData.subscription_id || '—'}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ mt: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                                        Paid via UPI / Netbanking / Wallet (Razorpay)
                                    </Typography>
                                </Box>
                            ) : userData?.stripe_customer_id ? (
                                <Box>
                                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem', mb: 1.5 }}>
                                        Stripe Customer ID
                                    </Typography>
                                    <Box sx={{
                                        p: 1.5, borderRadius: '10px',
                                        bgcolor: alpha(theme.palette.divider, isDark ? 0.12 : 0.06),
                                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                                    }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                            {userData.stripe_customer_id}
                                        </Typography>
                                    </Box>
                                    <Button
                                        fullWidth variant="outlined" size="small"
                                        onClick={handleManagePortal}
                                        endIcon={<PortalIcon sx={{ fontSize: 14 }} />}
                                        sx={{
                                            mt: 2.5, borderRadius: '10px', fontWeight: 700, fontSize: '0.8rem',
                                            borderColor: alpha(ACCENT, 0.35), color: ACCENT,
                                            '&:hover': { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.06) },
                                        }}
                                    >
                                        Manage in Customer Portal
                                    </Button>
                                </Box>
                            ) : (
                                <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    No payment method on file.
                                </Typography>
                            )}

                            <Divider sx={{ my: 3 }} />

                            {/* Invoices */}
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                                <Box sx={iconBoxSx()}>
                                    <HistoryIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                                </Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Invoices</Typography>
                            </Stack>
                            <Typography color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                                Your invoice history will appear here once your first payment is processed.
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ── AI Usage card ─────────────────────────────────────────────── */}
            {usageData && (
                <Card sx={{ ...cardSx, height: 'auto', position: 'relative', overflow: 'hidden' }}>
                    {/* glow blob */}
                    <Box sx={{
                        position: 'absolute', bottom: -80, left: -80, width: 260, height: 260,
                        borderRadius: '50%', pointerEvents: 'none',
                        background: `radial-gradient(circle, ${alpha(quotaColor, isDark ? 0.14 : 0.08)} 0%, transparent 70%)`,
                    }} />

                    <CardContent sx={{ p: 4, position: 'relative' }}>
                        {/* Header */}
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                            <Box sx={{
                                width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
                                bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.1),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: `0 4px 16px ${alpha(ACCENT, 0.2)}`,
                            }}>
                                <AIIcon sx={{ color: ACCENT, fontSize: 24 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 900, fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
                                    AI Usage This Month
                                </Typography>
                                <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                    Resets on {new Date(usageData.reset_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
                                </Typography>
                            </Box>
                        </Stack>

                        <Grid container spacing={3} alignItems="stretch">
                            {/* Progress panel */}
                            <Grid size={{ xs: 12, md: 7 }}>
                                <Box sx={{
                                    p: 3, borderRadius: '16px', height: '100%',
                                    bgcolor: alpha(theme.palette.divider, isDark ? 0.06 : 0.04),
                                    border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.1 : 0.15)}`,
                                }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Plan generations used</Typography>
                                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: quotaColor }}>
                                                {usageData.request_count.toLocaleString()}
                                            </Typography>
                                            <Typography color="text.secondary" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                / {usageData.plan_limit.toLocaleString()}
                                            </Typography>
                                        </Stack>
                                    </Stack>

                                    {/* Custom progress bar */}
                                    <Box sx={{
                                        position: 'relative', height: 10, borderRadius: '100px',
                                        bgcolor: alpha(theme.palette.divider, isDark ? 0.15 : 0.2),
                                        overflow: 'hidden', mb: 1.5,
                                    }}>
                                        <Box sx={{
                                            position: 'absolute', left: 0, top: 0, bottom: 0,
                                            width: `${usedPct}%`,
                                            borderRadius: '100px',
                                            background: usedPct >= 100
                                                ? 'linear-gradient(90deg, #ef4444, #f97316)'
                                                : usedPct > 80
                                                    ? 'linear-gradient(90deg, #f59e0b, #ef4444)'
                                                    : `linear-gradient(90deg, ${ACCENT}, ${GREEN})`,
                                            transition: 'width 0.8s ease',
                                            boxShadow: `0 0 10px ${alpha(quotaColor, 0.5)}`,
                                        }} />
                                    </Box>

                                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                        {usageData.plan_limit - usageData.request_count > 0
                                            ? `${(usageData.plan_limit - usageData.request_count).toLocaleString()} generations remaining`
                                            : 'Monthly plan quota exhausted — top-up credits are being used'}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Top-up Credits panel */}
                            <Grid size={{ xs: 12, md: 5 }}>
                                <Box sx={{
                                    p: 3, borderRadius: '16px', height: '100%',
                                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                    bgcolor: alpha(AMBER, isDark ? 0.1 : 0.06),
                                    border: `1px solid ${alpha(AMBER, usageData.topup_credits > 0 ? 0.3 : 0.15)}`,
                                }}>
                                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                                        <Box sx={iconBoxSx(AMBER)}>
                                            <TopupIcon sx={{ color: AMBER, fontSize: 22 }} />
                                        </Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                                            Top-up Credits
                                        </Typography>
                                    </Stack>

                                    <Typography sx={{
                                        fontWeight: 900, fontSize: '2.5rem', letterSpacing: '-0.03em', lineHeight: 1,
                                        color: usageData.topup_credits > 0 ? AMBER : theme.palette.text.disabled,
                                        mb: 0.5,
                                    }}>
                                        {usageData.topup_credits.toLocaleString()}
                                    </Typography>

                                    <Typography color="text.secondary" sx={{ fontSize: '0.78rem', mb: 2 }}>
                                        Never expire · used after plan runs out
                                    </Typography>

                                    {usageData.topup_credits === 0 && usageData.request_count >= usageData.plan_limit && (
                                        <Button
                                            variant="contained"
                                            component={Link}
                                            to="/app/pricing"
                                            startIcon={<TopupIcon />}
                                            sx={{
                                                borderRadius: '10px', fontWeight: 800, fontSize: '0.8rem',
                                                bgcolor: AMBER, color: '#000',
                                                boxShadow: `0 4px 14px ${alpha(AMBER, 0.4)}`,
                                                '&:hover': { bgcolor: '#fbbf24', boxShadow: `0 6px 20px ${alpha(AMBER, 0.5)}` },
                                            }}
                                        >
                                            Buy Top-up Credits
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Empty usage placeholder */}
            {!usageData && !loading && (
                <Card sx={{ ...cardSx, height: 'auto' }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <TrendingIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1.5 }} />
                        <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                            Usage data will appear here once you've made your first AI request.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* ── Invoices & Receipts ───────────────────────────────────────── */}
            <Card sx={{ ...cardSx, height: 'auto' }}>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <ReceiptIcon sx={{ color: ACCENT }} />
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Invoices & Receipts
                        </Typography>
                    </Box>

                    {loading ? (
                        /* Skeleton rows while auth/data loads */
                        <Table size="small">
                            <TableBody>
                                {[0,1,2].map(i => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton width={80} /></TableCell>
                                        <TableCell><Skeleton width={160} /></TableCell>
                                        <TableCell><Skeleton width={60} /></TableCell>
                                        <TableCell><Skeleton width={100} /></TableCell>
                                        <TableCell><Skeleton width={110} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : invoicesLoadError ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <ReceiptIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1.5 }} />
                            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                Couldn't load invoices. Please refresh to try again.
                            </Typography>
                        </Box>
                    ) : invoices.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <ReceiptIcon sx={{ fontSize: 40, color: theme.palette.text.disabled, mb: 1.5 }} />
                            <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                                No invoices or receipts yet. Subscribe to a plan or buy credits to get started.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem' }}>Date</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem' }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem' }}>Invoice #</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoices.map((inv) => {
                                    const date = inv.created_at
                                        ? new Date(inv.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                        : '—';
                                    const planRaw = inv.plan
                                        ? inv.plan.replace('_inr','').replace('_usd','').replace(/\b\w/g, c => c.toUpperCase()) + ' Plan'
                                        : null;
                                    const description = inv.type === 'topup'
                                        ? `Credit Top-up (${inv.credits || '?'} credits)`
                                        : (planRaw || 'Subscription');
                                    const currency = (inv.currency || 'usd').toUpperCase();
                                    const symbol = currency === 'INR' ? '₹' : '$';
                                    const amount = `${symbol}${((inv.amount_cents || 0) / 100).toFixed(2)}`;
                                    const hasPdf = !!inv.pdf_url;
                                    const pdfLoading = !!invoicesPdfLoading[inv.invoice_id];

                                    return (
                                        <TableRow key={inv.invoice_id} hover>
                                            <TableCell sx={{ fontSize: '0.82rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>{date}</TableCell>
                                            <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, maxWidth: 220 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Tooltip title={description} disableHoverListener={description.length <= 30}>
                                                        <Typography sx={{
                                                            fontSize: '0.82rem', fontWeight: 600,
                                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap', maxWidth: 160,
                                                        }}>
                                                            {description}
                                                        </Typography>
                                                    </Tooltip>
                                                    <Chip
                                                        label={inv.type === 'topup' ? 'Top-up' : 'Plan'}
                                                        size="small"
                                                        sx={{
                                                            flexShrink: 0,
                                                            fontSize: '0.65rem', fontWeight: 700, height: 18,
                                                            bgcolor: inv.type === 'topup'
                                                                ? alpha('#f59e0b', 0.12)
                                                                : alpha('#6366f1', 0.12),
                                                            color: inv.type === 'topup' ? '#d97706' : '#6366f1',
                                                        }}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={amount}
                                                    size="small"
                                                    sx={{ fontWeight: 800, bgcolor: alpha('#10b981', 0.12), color: '#059669', border: `1px solid ${alpha('#10b981', 0.2)}` }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', color: 'text.disabled', whiteSpace: 'nowrap' }}>{inv.invoice_number || '—'}</TableCell>
                                            <TableCell align="right">
                                                {hasPdf ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleDownloadPdf(inv)}
                                                        disabled={pdfLoading}
                                                        startIcon={pdfLoading
                                                            ? <CircularProgress size={12} color="inherit" />
                                                            : inv.type === 'topup' ? <PortalIcon sx={{ fontSize: 13 }} /> : <DownloadIcon sx={{ fontSize: 13 }} />
                                                        }
                                                        sx={{ borderRadius: '6px', fontWeight: 700, fontSize: '0.72rem', textTransform: 'none', minWidth: 120 }}
                                                    >
                                                        {inv.type === 'topup' ? 'View Receipt' : 'Download PDF'}
                                                    </Button>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        {invoicesHasMore && (
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleLoadMoreInvoices}
                                    disabled={invoicesLoadingMore}
                                    startIcon={invoicesLoadingMore ? <CircularProgress size={14} /> : null}
                                    sx={{ fontWeight: 700, color: ACCENT, textTransform: 'none' }}
                                >
                                    {invoicesLoadingMore ? 'Loading…' : 'Load more'}
                                </Button>
                            </Box>
                        )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Snackbar
                open={pdfErrorSnack}
                autoHideDuration={4000}
                onClose={() => setPdfErrorSnack(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setPdfErrorSnack(false)} sx={{ width: '100%' }}>
                    Failed to download PDF. Please try again.
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Billing;
